import { TFunction } from 'i18next';
import {
  EvaluationTagDto,
  StartEvaluationContext,
  TemplateQuestionSummary,
  TemplateSummary,
  WizardBody,
  WizardProject,
} from '../../services/service-evaluations';
import {
  EvaluationCommentRule,
  EvaluationContextType,
  EvaluationLimits,
  EvaluationQuestionType,
} from '../../util/evaluationContract';
import { WizardSeed, WizardSeedSupplier } from '../../util/evaluationsNavigation';
import {
  RecipientOption,
  defaultDueDay,
  defaultTitle,
  toRecipientInputs,
} from '../app-evaluation-start/evaluationStart';

export const LAST_STEP = 4;

export type ContextChoice = 'project' | 'historical' | 'none';
export type TemplateChoice = { kind: 'template'; templateId: string } | { kind: 'own' };
export type WizardSupplier = WizardSeedSupplier;
export type ExtraQuestion = { _id: string; text: string };
export type PreviewQuestion = TemplateQuestionSummary & { added?: true; removed?: true };

export type WizardState = {
  step: number;
  maxReached: number;
  contextChoice: ContextChoice | null;
  project: WizardProject | null;
  historicalName: string;
  historicalNumber: string;
  // Read only once tagsTouched; until then the chosen template's suggestions are the tags
  tagIds: string[];
  tagsTouched: boolean;
  suppliers: WizardSupplier[];
  templateChoice: TemplateChoice | null;
  extraQuestions: ExtraQuestion[];
  // For this send only, on the chosen template; cleared when another card is chosen
  removedQuestionKeys: string[];
  requiredByKey: Record<string, boolean>;
  allowSkipByKey: Record<string, boolean>;
  ownQuestionIds: string[];
  recipients: RecipientOption[];
  dueDay: string;
  // null until edited: the default message is shown and sent
  message: string | null;
  editedTitle: string | null;
  projectToLoad: string | null;
  sending: boolean;
  notice: 'seeded' | 'contextCleared' | 'projectUnavailable' | null;
};

export type WizardData = { templates: TemplateSummary[]; tags: EvaluationTagDto[]; today: string };

export type MissingItem =
  | {
      key:
        | 'contextType'
        | 'project'
        | 'historical'
        | 'suppliers'
        | 'template'
        | 'questions'
        | 'noRating'
        | 'recipients'
        | 'dueDate';
    }
  | { key: 'tooManyExtras'; count: number };

export type WizardAction =
  | { type: 'goTo'; step: number }
  | { type: 'chooseContext'; choice: ContextChoice }
  | { type: 'setProject'; project: WizardProject | null }
  | { type: 'setHistorical'; historicalName: string; historicalNumber: string }
  | { type: 'setTags'; tagIds: string[] }
  | { type: 'setSuppliers'; suppliers: WizardSupplier[] }
  | { type: 'chooseTemplate'; choice: TemplateChoice }
  | { type: 'setExtras'; extras: ExtraQuestion[] }
  | { type: 'toggleRemoved'; key: string }
  | { type: 'setRequired'; key: string; required: boolean }
  | { type: 'setAllowSkip'; key: string; allowSkip: boolean }
  | { type: 'setOwnQuestions'; ids: string[] }
  | { type: 'setRecipients'; recipients: RecipientOption[] }
  | { type: 'setDueDay'; dueDay: string }
  | { type: 'setMessage'; message: string }
  | { type: 'setTitle'; title: string }
  | {
      type: 'projectLoaded';
      project: WizardProject | null;
      data: WizardData;
      preselectWinners: boolean;
    }
  | { type: 'setSending'; sending: boolean };

export const initialWizardState = (today: string): WizardState => ({
  step: 0,
  maxReached: 0,
  contextChoice: null,
  project: null,
  historicalName: '',
  historicalNumber: '',
  tagIds: [],
  tagsTouched: false,
  suppliers: [],
  templateChoice: null,
  extraQuestions: [],
  removedQuestionKeys: [],
  requiredByKey: {},
  allowSkipByKey: {},
  ownQuestionIds: [],
  recipients: [],
  dueDay: defaultDueDay(new Date(`${today}T12:00:00`)),
  message: null,
  editedTitle: null,
  projectToLoad: null,
  sending: false,
  notice: null,
});

export const templateOf = (state: WizardState, data: WizardData): TemplateSummary | null => {
  const choice = state.templateChoice;
  if (choice?.kind !== 'template') return null;
  return data.templates.find(template => template._id === choice.templateId) ?? null;
};

export const extraRoom = (template: TemplateSummary, removedQuestionKeys: string[]) =>
  EvaluationLimits.MaxQuestionsPerRequest -
  template.questions.filter(question => !removedQuestionKeys.includes(question.key)).length;

export const suggestedTagIds = (template: TemplateSummary | null, tags: EvaluationTagDto[]) =>
  (template?.suggestedTagIds ?? [])
    .filter(id => tags.some(tag => tag._id === id && !tag.archived))
    .slice(0, EvaluationLimits.MaxTagsPerRequest);

export const effectiveTagIds = (state: WizardState, data: WizardData) =>
  state.tagsTouched ? state.tagIds : suggestedTagIds(templateOf(state, data), data.tags);

export const missingFor = (step: number, state: WizardState, data: WizardData): MissingItem[] => {
  if (step === 0) {
    if (!state.contextChoice) return [{ key: 'contextType' }];
    if (state.contextChoice === 'project' && !state.project) return [{ key: 'project' }];
    if (
      state.contextChoice === 'historical' &&
      !state.historicalName.trim() &&
      !state.historicalNumber.trim()
    ) {
      return [{ key: 'historical' }];
    }
    return [];
  }
  if (step === 1) return state.suppliers.length ? [] : [{ key: 'suppliers' }];
  if (step === 2) {
    if (state.templateChoice?.kind === 'own') {
      return state.ownQuestionIds.length ? [] : [{ key: 'questions' }];
    }
    const template = templateOf(state, data);
    if (!template) return [{ key: 'template' }];
    const items: MissingItem[] = [];
    const over = state.extraQuestions.length - extraRoom(template, state.removedQuestionKeys);
    if (over > 0) items.push({ key: 'tooManyExtras', count: over });
    const sent = previewQuestions(template, state).filter(question => !question.removed);
    if (
      state.removedQuestionKeys.length &&
      !sent.some(question => question.type === EvaluationQuestionType.Rating)
    ) {
      items.push({ key: 'noRating' });
    }
    return items;
  }
  if (step === 3) {
    const items: MissingItem[] = [];
    if (!state.recipients.length) items.push({ key: 'recipients' });
    if (!state.dueDay || state.dueDay <= data.today) items.push({ key: 'dueDate' });
    return items;
  }
  return [];
};

export const missingAll = (state: WizardState, data: WizardData) =>
  [0, 1, 2, 3].flatMap(step => missingFor(step, state, data));

export const firstIncompleteStep = (state: WizardState, data: WizardData) => {
  for (let step = 0; step < LAST_STEP; step += 1) {
    if (missingFor(step, state, data).length) return step;
  }
  return LAST_STEP;
};

export const missingText = (t: TFunction, item: MissingItem): string => {
  switch (item.key) {
    case 'contextType':
      return t('wizard.missing.contextType', 'välj typ av projekt');
    case 'project':
      return t('quick.missing.project', 'välj projekt');
    case 'historical':
      return t('quick.missing.historical', 'skriv projektnamn eller nummer');
    case 'suppliers':
      return t('wizard.missing.suppliers', 'välj minst en leverantör');
    case 'template':
      return t('wizard.missing.template', 'välj en mall eller Egna frågor');
    case 'questions':
      return t('quick.missing.questions', 'välj minst en fråga');
    case 'noRating':
      return t('wizard.missing.noRating', 'behåll minst en betygsfråga');
    case 'tooManyExtras':
      return t('wizard.missing.tooManyExtras', {
        count: item.count,
        defaultValue_one: 'ta bort {{count}} tillagd fråga (högst 40 frågor per utvärdering)',
        defaultValue_other: 'ta bort {{count}} tillagda frågor (högst 40 frågor per utvärdering)',
      });
    case 'recipients':
      return t('quick.missing.recipients', 'lägg till mottagare');
    default:
      return t('wizard.missing.dueDate', 'välj en sista svarsdag i morgon eller senare');
  }
};

export const areaIdFor = (
  project: WizardProject | null,
  supplier: WizardSupplier,
): string | null => {
  if (!project) return null;
  if (project.areas.some(area => area.areaId === supplier.areaId)) return supplier.areaId ?? null;
  const holding = project.areas.filter(area => area.supplierIds.includes(supplier._id));
  return holding.length === 1 ? holding[0].areaId : null;
};

const SUPPLIERS_STEP = 1;

const unevaluatedWinners = (project: WizardProject): WizardSupplier[] => {
  const picked = new Map<string, WizardSupplier>();
  project.areas.forEach(area =>
    area.winners.forEach(winner => {
      if (winner.evaluated || picked.has(winner.supplierId)) return;
      picked.set(winner.supplierId, {
        _id: winner.supplierId,
        name: winner.name,
        areaId: area.areaId,
      });
    }),
  );
  return Array.from(picked.values());
};

export const projectWinners = (project: WizardProject) =>
  unevaluatedWinners(project).slice(0, EvaluationLimits.MaxSuppliersPerSend);

const withoutPurchase = ({ _id, name }: WizardSupplier): WizardSupplier => ({ _id, name });

const keepingPurchasesOf = (project: WizardProject, suppliers: WizardSupplier[]) =>
  suppliers.map(supplier =>
    project.areas.some(area => area.areaId === supplier.areaId)
      ? supplier
      : withoutPurchase(supplier),
  );

export const addedQuestionKey = (questionId: string) => `added-${questionId}`;

const addedQuestion = (extra: ExtraQuestion): PreviewQuestion => ({
  key: addedQuestionKey(extra._id),
  text: extra.text,
  type: EvaluationQuestionType.Rating,
  required: true,
  allowSkip: true,
  commentRule: EvaluationCommentRule.None,
  added: true,
});

// Same rule as startWizard on the server: extras go before a closing free-text question, else last
export const placeExtras = (
  questions: PreviewQuestion[],
  extras: ExtraQuestion[],
): PreviewQuestion[] => {
  if (!extras.length) return questions;
  const added = extras.map(addedQuestion);
  const kept = questions.filter(question => !question.removed);
  const last = kept[kept.length - 1];
  if (last?.type !== EvaluationQuestionType.Text) return [...questions, ...added];
  const at = questions.indexOf(last);
  return [...questions.slice(0, at), ...added, ...questions.slice(at)];
};

// Removed rows stay in place, so the step can offer Ångra where the question was
export const previewQuestions = (
  template: TemplateSummary,
  state: WizardState,
): PreviewQuestion[] =>
  placeExtras(
    template.questions.map(question =>
      state.removedQuestionKeys.includes(question.key) ? { ...question, removed: true } : question,
    ),
    state.extraQuestions,
  ).map(question => ({
    ...question,
    required: state.requiredByKey[question.key] ?? question.required,
    allowSkip: state.allowSkipByKey[question.key] ?? question.allowSkip,
  }));

const withoutRemoved = (state: WizardState, byKey: Record<string, boolean>) =>
  Object.fromEntries(
    Object.entries(byKey).filter(([key]) => !state.removedQuestionKeys.includes(key)),
  );

type Me = { userId: string | null; email: string | null };

const isMe = (recipient: RecipientOption, me: Me) =>
  Boolean(
    (me.userId && recipient._id === me.userId) ||
      (me.email && (recipient.email ?? '').toLowerCase() === me.email.toLowerCase()),
  );

export const mailCount = (state: WizardState, me: Me) => {
  const people = state.recipients.filter(recipient => !isMe(recipient, me)).length;
  return {
    mails: state.suppliers.length * people,
    suppliers: state.suppliers.length,
    people,
    selfIncluded: people !== state.recipients.length,
  };
};

export const contextName = (state: WizardState): string | null => {
  if (state.contextChoice === 'project') return state.project?.name ?? null;
  if (state.contextChoice === 'historical') {
    return state.historicalName.trim() || state.historicalNumber.trim() || null;
  }
  return null;
};

export const defaultMessage = (t: TFunction, state: WizardState) => {
  const supplier =
    state.suppliers.length === 1
      ? state.suppliers[0].name
      : t('wizard.recipients.severalSuppliers', 'leverantören');
  const project = contextName(state);
  return project
    ? t(
        'wizard.recipients.defaultMessage',
        'Dela gärna dina erfarenheter av {{supplier}} i projektet {{project}}. Det tar ungefär tre minuter.',
        { supplier, project },
      )
    : t(
        'wizard.recipients.defaultMessageNoProject',
        'Dela gärna dina erfarenheter av {{supplier}}. Det tar ungefär tre minuter.',
        { supplier },
      );
};

export const seededText = (t: TFunction, state: WizardState) => {
  const context =
    state.contextChoice === 'none' ? t('wizard.context.none', 'Inget projekt') : contextName(state);
  const items = [state.suppliers.map(supplier => supplier.name).join(', '), context].filter(
    Boolean,
  );
  return t('wizard.context.seeded', 'Från Ny utvärdering: {{items}}', {
    items: items.join(' · '),
  });
};

// One supplier gets a generated title; several leave it empty so the server names each after its supplier
export const titleValue = (t: TFunction, state: WizardState) =>
  state.suppliers.length === 1
    ? state.editedTitle ?? defaultTitle(t, state.suppliers[0].name, contextName(state))
    : state.editedTitle ?? '';

const contextOf = (state: WizardState): StartEvaluationContext => {
  if (state.contextChoice === 'project' && state.project) {
    return { type: EvaluationContextType.Project, projectId: state.project._id };
  }
  if (state.contextChoice === 'historical') {
    return {
      type: EvaluationContextType.Historical,
      historical: { name: state.historicalName.trim(), number: state.historicalNumber.trim() },
    };
  }
  return { type: EvaluationContextType.None };
};

export const toWizardBody = (
  state: WizardState,
  data: WizardData,
  t: TFunction,
): Omit<WizardBody, 'idempotencyKey'> => {
  const project = state.contextChoice === 'project' ? state.project : null;
  const title = titleValue(t, state).trim();
  const choice = state.templateChoice;
  return {
    suppliers: state.suppliers.map(supplier => ({
      supplierId: supplier._id,
      areaId: areaIdFor(project, supplier),
    })),
    context: contextOf(state),
    ...(choice?.kind === 'template' && { templateId: choice.templateId }),
    questionIds:
      choice?.kind === 'template'
        ? state.extraQuestions.map(question => question._id)
        : state.ownQuestionIds,
    removedQuestionKeys: choice?.kind === 'template' ? state.removedQuestionKeys : [],
    requiredByKey: choice?.kind === 'template' ? withoutRemoved(state, state.requiredByKey) : {},
    allowSkipByKey: choice?.kind === 'template' ? withoutRemoved(state, state.allowSkipByKey) : {},
    tagIds: effectiveTagIds(state, data),
    recipients: toRecipientInputs(state.recipients),
    dueDayKey: state.dueDay,
    title:
      title || (state.suppliers.length === 1 ? titleValue(t, { ...state, editedTitle: null }) : ''),
    message: state.message ?? defaultMessage(t, state),
  };
};

export const seedToState = (seed: WizardSeed | null, today: string): WizardState => {
  const initial = initialWizardState(today);
  if (!seed) return initial;
  const projectToLoad = seed.project?._id ?? null;
  const seeded: WizardState = {
    ...initial,
    contextChoice: projectToLoad ? 'project' : null,
    suppliers: projectToLoad ? seed.suppliers : seed.suppliers.map(withoutPurchase),
    projectToLoad,
  };
  if (projectToLoad) return seeded;
  const step = firstIncompleteStep(seeded, { templates: [], tags: [], today });
  return { ...seeded, step, maxReached: step, notice: 'seeded' };
};

const EDITABLE: (keyof WizardState)[] = [
  'contextChoice',
  'project',
  'historicalName',
  'historicalNumber',
  'tagIds',
  'tagsTouched',
  'suppliers',
  'templateChoice',
  'extraQuestions',
  'removedQuestionKeys',
  'requiredByKey',
  'allowSkipByKey',
  'ownQuestionIds',
  'recipients',
  'dueDay',
  'message',
  'editedTitle',
];

export const isDirty = (state: WizardState, initial: WizardState) =>
  EDITABLE.some(key => JSON.stringify(state[key]) !== JSON.stringify(initial[key]));

const CLEARED_CONTEXT = {
  project: null,
  historicalName: '',
  historicalNumber: '',
  projectToLoad: null,
};

const edited = (state: WizardState, change: Partial<WizardState>): WizardState => ({
  ...state,
  ...change,
  notice: null,
});

export const wizardReducer = (state: WizardState, action: WizardAction): WizardState => {
  switch (action.type) {
    case 'goTo':
      return {
        ...state,
        step: action.step,
        maxReached: Math.max(state.maxReached, action.step),
        notice: null,
      };
    case 'chooseContext': {
      if (state.contextChoice === action.choice) return state;
      const hadDetails = Boolean(
        state.project || state.projectToLoad || state.historicalName || state.historicalNumber,
      );
      return {
        ...state,
        ...CLEARED_CONTEXT,
        suppliers: state.suppliers.map(withoutPurchase),
        contextChoice: action.choice,
        notice: hadDetails ? 'contextCleared' : null,
      };
    }
    case 'setProject':
      if (state.project?._id === action.project?._id) return state;
      return edited(state, {
        project: action.project,
        suppliers: state.suppliers.map(withoutPurchase),
      });
    case 'setHistorical':
      return edited(state, {
        historicalName: action.historicalName,
        historicalNumber: action.historicalNumber,
      });
    case 'setTags':
      return edited(state, { tagIds: action.tagIds, tagsTouched: true });
    case 'setSuppliers':
      return edited(state, { suppliers: action.suppliers });
    case 'chooseTemplate':
      if (JSON.stringify(state.templateChoice) === JSON.stringify(action.choice)) {
        return edited(state, { templateChoice: action.choice });
      }
      return edited(state, {
        templateChoice: action.choice,
        removedQuestionKeys: [],
        requiredByKey: {},
        allowSkipByKey: {},
      });
    case 'setExtras': {
      const keptKeys = action.extras.map(extra => addedQuestionKey(extra._id));
      const ofKeptQuestions = (byKey: Record<string, boolean>) =>
        Object.fromEntries(
          Object.entries(byKey).filter(
            ([key]) => !key.startsWith(addedQuestionKey('')) || keptKeys.includes(key),
          ),
        );
      return edited(state, {
        extraQuestions: action.extras,
        requiredByKey: ofKeptQuestions(state.requiredByKey),
        allowSkipByKey: ofKeptQuestions(state.allowSkipByKey),
      });
    }
    case 'toggleRemoved':
      return edited(state, {
        removedQuestionKeys: state.removedQuestionKeys.includes(action.key)
          ? state.removedQuestionKeys.filter(key => key !== action.key)
          : [...state.removedQuestionKeys, action.key],
      });
    case 'setRequired':
      return edited(state, {
        requiredByKey: { ...state.requiredByKey, [action.key]: action.required },
      });
    case 'setAllowSkip':
      return edited(state, {
        allowSkipByKey: { ...state.allowSkipByKey, [action.key]: action.allowSkip },
      });
    case 'setOwnQuestions':
      return edited(state, { ownQuestionIds: action.ids });
    case 'setRecipients':
      return edited(state, { recipients: action.recipients });
    case 'setDueDay':
      return edited(state, { dueDay: action.dueDay });
    case 'setMessage':
      return edited(state, { message: action.message });
    case 'setTitle':
      return edited(state, { editedTitle: action.title });
    case 'projectLoaded': {
      if (!state.projectToLoad) return state;
      const { project } = action;
      if (!project || project._id !== state.projectToLoad) {
        return {
          ...state,
          ...CLEARED_CONTEXT,
          contextChoice: null,
          step: 0,
          maxReached: 0,
          notice: 'projectUnavailable',
        };
      }
      const loaded: WizardState = {
        ...state,
        project,
        suppliers: action.preselectWinners
          ? projectWinners(project)
          : keepingPurchasesOf(project, state.suppliers),
        projectToLoad: null,
      };
      // Winners left out by the cap are named only on Leverantörer
      const capped =
        action.preselectWinners &&
        unevaluatedWinners(project).length > EvaluationLimits.MaxSuppliersPerSend;
      const step = capped ? SUPPLIERS_STEP : firstIncompleteStep(loaded, action.data);
      return { ...loaded, step, maxReached: step, notice: 'seeded' };
    }
    case 'setSending':
      return { ...state, sending: action.sending };
    default:
      return state;
  }
};
